import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Alert,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  Divider,
  Badge,
  Tooltip,
  Fab,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Breadcrumbs,
  Link,
  Drawer,
  AppBar,
  Toolbar,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  TextareaAutosize,
} from '@mui/material';
import {
  Add,
  Search,
  FilterList,
  Visibility,
  Edit,
  Delete,
  Download,
  MoreVert,
  Description,
  Image,
  VideoFile,
  AudioFile,
  Archive,
  Email,
  CloudUpload,
  ViewModule,
  ViewList,
  Folder,
  FileCopy,
  Share,
  Lock,
  Public,
  Assignment,
  Person,
  CalendarToday,
  GetApp,
  Create,
  PictureAsPdf,
  TextFields,
  TableChart,
  ExpandMore,
  Note,
  NoteAdd,
  FolderOpen,
  Home,
  NavigateNext,
  Info,
  Edit as EditIcon,
  CloudDownload,
  AttachMoney,
  Gavel,
  Business,
  Schedule,
  ChevronLeft,
  ChevronRight,
  ArrowBack,
  Category,
  Label,
  Autorenew,
  AssignmentTurnedIn,
  LocalOffer,
  Gavel as GavelIcon,
  FolderZip,
  Draw,
  LibraryAddCheck,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import DocumentDialog from '../Documents/DocumentDialog';
import DocumentUpload from '../Documents/DocumentUpload';
import DocumentEditor from '../Documents/DocumentEditor';
import DocumentGallery from '../Documents/DocumentGallery';
import ExportCaseFileDialog from './ExportCaseFileDialog';
import DocumentPreviewDialog from '../Documents/DocumentPreviewDialog';
import ImportTemplateToCaseDialog from '../Documents/ImportTemplateToCaseDialog';
import AttachExistingDocumentsDialog from '../Documents/AttachExistingDocumentsDialog';
import ListPagination from '../Common/ListPagination';
import CaseIntakeWizard from './CaseIntakeWizard';
import CasePropertiesPanel from './CasePropertiesPanel';
import PrivilegeLogPanel from './PrivilegeLogPanel';
import CaseWorkflowPanel from './CaseWorkflowPanel';
import CallLogPanel from './CallLogPanel';
import CaseActivityPanel from './CaseActivityPanel';
import CaseCourtFormsPanel from './CaseCourtFormsPanel';
import BatesNumberingDialog, { getBatesDisplay } from '../Documents/BatesNumberingDialog';
import AutoCaptionDialog, { getCaptionDisplay } from '../Documents/AutoCaptionDialog';
import DateCalculatorDialog from '../Calendar/DateCalculatorDialog';
import RequestSignatureDialog from '../Documents/RequestSignatureDialog';
import SignatureStatusChip from '../Documents/SignatureStatusChip';
import ReviewStatusChip from '../Documents/ReviewStatusChip';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { userHasPermission } from '../../utils/permissions';
import { getAuthToken } from '../../lib/authToken';

interface Case {
  id: string;
  caseNumber: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  caseType: string;
  jurisdiction?: string;
  clientId?: string | null;
  client?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignedUsers?: string[];
  assignedUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  estimatedValue?: number;
  filingDate?: string;
  closingDate?: string;
  court?: string;
  judge?: string;
  tags?: string[] | string;
  solExpirationDate?: string | null;
  solIncidentDate?: string | null;
  solPresetId?: string | null;
  solCalculatedAt?: string | null;
  representationStatus?: string | null;
  retainerSignedAt?: string | null;
  metadata?: Record<string, unknown> | null;
}

interface Document {
  id: string;
  title: string;
  description: string;
  fileName: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  documentType: string;
  category: string;
  tags: string[] | string;
  isConfidential: boolean;
  isPublic: boolean;
  isTemplate: boolean;
  version: number;
  caseId: string | null;
  clientId: string | null;
  uploadedBy: string;
  createdAt: string | null;
  updatedAt: string | null;
  reviewStatus?: string;
  metadata?: Record<string, unknown>;
}

interface CaseNote {
  id: string;
  title: string;
  content: string;
  noteType: string;
  category: string;
  tags: string[];
  isConfidential: boolean;
  documentId: string | null;
  createdAt: string;
  updatedAt: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  document?: {
    id: string;
    title: string;
    originalName: string;
  };
}

interface CaseFolderProps {
  caseId: string;
  caseData: Case;
  /** Compact layout when nested inside View Case dialog (no sidebar / 100vh). */
  embedded?: boolean;
}

const CaseFolder: React.FC<CaseFolderProps> = ({ caseId, caseData, embedded = false }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tenant } = useTenant();
  const eSignEnabled = Boolean(tenant?.settings?.features?.eSignature);
  const canDeleteDocuments = userHasPermission(user, 'documents:delete');
  const canWriteCases = userHasPermission(user, 'cases:write');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit' | 'create'>('edit');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<Document | CaseNote | null>(null);
  
  // Notes functionality
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<CaseNote | null>(null);
  const [noteMode, setNoteMode] = useState<'create' | 'edit' | 'view'>('create');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<CaseNote | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [batesDialogOpen, setBatesDialogOpen] = useState(false);
  const [captionDialogOpen, setCaptionDialogOpen] = useState(false);
  const [solCalculatorOpen, setSolCalculatorOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [docPage, setDocPage] = useState(1);
  const [docLimit, setDocLimit] = useState(50);
  const [notePage, setNotePage] = useState(1);
  const [noteLimit, setNoteLimit] = useState(50);
  const [refreshingMerge, setRefreshingMerge] = useState(false);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signDocumentId, setSignDocumentId] = useState<string | null>(null);
  const [noteForm, setNoteForm] = useState({
    title: '',
    content: '',
    noteType: 'general',
    category: '',
    tags: [] as string[],
    isConfidential: false,
    documentId: null as string | null,
  });

  // Fetch case documents (only documents for this specific case)
  const { data: documentsData, isLoading: documentsLoading, error: documentsError } = useQuery({
    queryKey: ['case-documents', caseId, docPage, docLimit],
    queryFn: async () => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const params = new URLSearchParams({ page: String(docPage), limit: String(docLimit) });
      const response = await fetch(`/api/case-documents/${caseId}/documents?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch case documents');
      }

      return response.json();
    },
  });

  const { data: signatureData } = useQuery({
    queryKey: ['signature-requests', caseId],
    enabled: Boolean(caseId) && eSignEnabled,
    queryFn: async () => {
      const token = getAuthToken();
      const response = await fetch(`/api/signature-requests?caseId=${caseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 403) return { data: [] };
      if (!response.ok) throw new Error('Failed to fetch signature requests');
      return response.json();
    },
  });

  // Fetch case notes
  const { data: notesData, isLoading: notesLoading, error: notesError } = useQuery({
    queryKey: ['case-notes', caseId, notePage, noteLimit],
    queryFn: async () => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      // The notes endpoint also defaults to a small page; ask explicitly so the
      // tab count matches what the firm actually filed.
      const params = new URLSearchParams({ page: String(notePage), limit: String(noteLimit) });
      const response = await fetch(`/api/case-documents/${caseId}/notes?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch case notes');
      }

      return response.json();
    },
  });

  const { data: caseProperties = [] } = useQuery({
    queryKey: ['case-properties', caseId],
    enabled: Boolean(caseId),
    queryFn: async () => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');
      const response = await fetch(`/api/case-intake/${caseId}/properties`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return [];
      const json = await response.json();
      return Array.isArray(json.data) ? json.data : [];
    },
  });

  // Delete document mutation
  const deleteDocumentMutation = useMutation({
    mutationFn: async (documentId: string) => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`/api/case-documents/${caseId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['case-documents', caseId]);
      toast.success('Document deleted successfully');
      setAnchorEl(null);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete document');
    },
  });

  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`/api/case-documents/${caseId}/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete note');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['case-notes', caseId]);
      toast.success('Note deleted successfully');
      setAnchorEl(null);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete note');
    },
  });

  // Create/Update note mutation
  const noteMutation = useMutation({
    mutationFn: async (noteData: Partial<CaseNote>) => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const url = noteMode === 'create' 
        ? `/api/case-documents/${caseId}/notes`
        : `/api/case-documents/${caseId}/notes/${selectedNote?.id}`;
      
      const method = noteMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(noteData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to save note');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch the notes query
      queryClient.invalidateQueries(['case-notes', caseId]);
      toast.success(noteMode === 'create' ? 'Note created successfully' : 'Note updated successfully');
      setNoteDialogOpen(false);
      resetNoteForm();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save note');
    },
  });

  const handleEditDocument = (document: Document) => {
    setSelectedDocument(document);
    setEditorOpen(true);
  };

  const handleViewDocument = (document: Document) => {
    setPreviewDocument(document);
    setPreviewOpen(true);
  };

  const handleEditDocumentProperties = (document: Document) => {
    setSelectedDocument(document);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const downloadCaseDocument = async (doc: Document) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`/api/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Download failed'));
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = doc.originalName || doc.fileName || 'document';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err.message || 'Failed to download document');
    }
  };

  const handleRefreshMerge = async (doc: Document) => {
    setRefreshingMerge(true);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `/api/case-documents/${caseId}/documents/${doc.id}/refresh-merge`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to refresh merge.'));
      }
      queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] });
      toast.success('Document placeholders refreshed from template');
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Failed to refresh merge.'));
    } finally {
      setRefreshingMerge(false);
      setAnchorEl(null);
    }
  };

  const handleCreateNote = () => {
    setNoteMode('create');
    setSelectedNote(null);
    resetNoteForm();
    setNoteDialogOpen(true);
  };

  const handleEditNote = (note: CaseNote) => {
    setNoteMode('edit');
    setSelectedNote(note);
    setNoteForm({
      title: note.title,
      content: note.content,
      noteType: note.noteType,
      category: note.category,
      tags: note.tags,
      isConfidential: note.isConfidential,
      documentId: note.documentId,
    });
    setNoteDialogOpen(true);
  };

  const handleViewNote = (note: CaseNote) => {
    setNoteMode('view');
    setSelectedNote(note);
    setNoteForm({
      title: note.title,
      content: note.content,
      noteType: note.noteType,
      category: note.category,
      tags: note.tags,
      isConfidential: note.isConfidential,
      documentId: note.documentId,
    });
    setNoteDialogOpen(true);
  };

  const handleSaveNote = () => {
    noteMutation.mutate(noteForm);
  };

  const resetNoteForm = () => {
    setNoteForm({
      title: '',
      content: '',
      noteType: 'general',
      category: '',
      tags: [],
      isConfidential: false,
      documentId: null,
    });
  };

  const getDocumentTypeIcon = (documentType: string) => {
    switch (documentType) {
      case 'pleading':
      case 'court_filing':
        return <Description color="primary" />;
      case 'contract':
      case 'agreement':
        return <Assignment color="secondary" />;
      case 'evidence':
        return <Image color="success" />;
      case 'photograph':
        return <Image color="success" />;
      case 'video':
        return <VideoFile color="error" />;
      case 'audio':
        return <AudioFile color="warning" />;
      case 'email_attachment':
        return <Email color="info" />;
      default:
        return <Description />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  const documents = documentsData?.data?.documents || [];
  const notes = notesData?.data?.notes || [];
  // Tab counts must report the server total, not the page that came back, so a
  // firm can tell at a glance that nothing went missing.
  const documentsTotal = documentsData?.data?.pagination?.total ?? documents.length;
  const notesTotal = notesData?.data?.pagination?.total ?? notes.length;
  const signatureRequests: Array<{ id: string; status: string; sourceDocumentId: string; signedDocumentId?: string }> =
    signatureData?.data || [];
  const sigByDoc = new Map(signatureRequests.map((r) => [r.sourceDocumentId, r]));
  const signedIds = new Set(signatureRequests.map((r) => r.signedDocumentId).filter(Boolean));

  return (
    <Box
      sx={{
        height: embedded ? '100%' : '100vh',
        maxHeight: embedded ? '100%' : undefined,
        display: 'flex',
        flexDirection: 'column',
        minHeight: embedded ? 0 : undefined,
        overflow: 'hidden',
      }}
    >
      {/* Case Folder Header */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar
          variant={embedded ? 'dense' : 'regular'}
          sx={{ flexWrap: 'wrap', gap: 1, py: embedded ? 1 : 0 }}
        >
          {!embedded && (
            <>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => navigate('/cases')}
                sx={{ mr: 1 }}
              >
                <ArrowBack />
              </IconButton>
              <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mr: 2 }}>
                <Button
                  onClick={() => navigate('/cases')}
                  color="inherit"
                  sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                >
                  Cases
                </Button>
                <Typography color="text.primary">{caseData.caseNumber}</Typography>
              </Breadcrumbs>
            </>
          )}
          {embedded && (
            <Typography variant="subtitle1" sx={{ mr: 1, fontWeight: 600 }}>
              {caseData.caseNumber}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            size={embedded ? 'small' : 'medium'}
            startIcon={<CloudUpload />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload
          </Button>
          <Button
            variant="outlined"
            size={embedded ? 'small' : 'medium'}
            startIcon={<LibraryAddCheck />}
            onClick={() => setAttachDialogOpen(true)}
          >
            Add Existing
          </Button>
          <Button
            variant="outlined"
            size={embedded ? 'small' : 'medium'}
            startIcon={<FolderZip />}
            onClick={() => setExportDialogOpen(true)}
          >
            Export Case File
          </Button>
          <Button
            variant="outlined"
            size={embedded ? 'small' : 'medium'}
            startIcon={<Description />}
            onClick={() => setTemplateDialogOpen(true)}
          >
            Import Template
          </Button>
          <Button
            variant="outlined"
            size={embedded ? 'small' : 'medium'}
            startIcon={<AssignmentTurnedIn />}
            onClick={() => setIntakeOpen(true)}
          >
            Continue Intake
          </Button>
          <Button
            variant="outlined"
            size={embedded ? 'small' : 'medium'}
            startIcon={<NoteAdd />}
            onClick={handleCreateNote}
          >
            Add Note
          </Button>
          {eSignEnabled && caseData.representationStatus === 'awaiting_engagement' && (user?.role === 'admin' || user?.role === 'lawyer') && (
            <Button
              color="warning"
              variant="outlined"
              size="small"
              startIcon={<Gavel />}
              onClick={async () => {
                const token = getAuthToken();
                const res = await fetch(`/api/cases/${caseId}/accept-engagement`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                });
                if (!res.ok) {
                  toast.error(await getApiErrorMessage(res, 'Could not confirm engagement'));
                  return;
                }
                toast.success('Engagement confirmed');
                queryClient.invalidateQueries({ queryKey: ['cases'] });
                queryClient.invalidateQueries({ queryKey: ['case', caseId] });
                queryClient.invalidateQueries({ queryKey: ['signature-requests', caseId] });
              }}
            >
              Confirm engagement
            </Button>
          )}
          {embedded && (
            <Button
              variant="text"
              size="small"
              startIcon={<FolderOpen />}
              onClick={() => navigate(`/cases/${caseId}/folder`)}
            >
              Open full folder
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* Case Summary Sidebar — full-page only */}
        {!embedded && (
        <Drawer
          variant="permanent"
          sx={{
            width: sidebarCollapsed ? 60 : 350,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: sidebarCollapsed ? 60 : 350,
              boxSizing: 'border-box',
              transition: 'width 0.3s ease',
              overflowY: 'auto',
              overflowX: 'hidden',
            },
          }}
        >
          <Box sx={{ p: 3, position: 'relative', pb: 6 }}>
            {/* Toggle Button */}
            <Tooltip title={sidebarCollapsed ? "Expand Case Summary" : "Collapse Case Summary"}>
              <IconButton
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  backgroundColor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
              </IconButton>
            </Tooltip>
            
            {!sidebarCollapsed && (
              <Typography variant="h6" gutterBottom>
                Case Summary
              </Typography>
            )}
            
            {sidebarCollapsed && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Gavel sx={{ color: 'primary.main', fontSize: 24 }} />
              </Box>
            )}
            
            {!sidebarCollapsed && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Gavel sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {caseData.caseNumber}
                    </Typography>
                  </Box>
                
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {caseData.title}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {caseData.description}
                </Typography>

                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={caseData.status.replace('_', ' ')} 
                    color="primary"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Priority</Typography>
                  <Chip 
                    label={caseData.priority} 
                    color="secondary"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Case Type</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {caseData.caseType}
                  </Typography>
                </Box>

                {caseData.jurisdiction && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Jurisdiction</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {caseData.jurisdiction === 'federal'
                        ? 'Federal'
                        : caseData.jurisdiction}
                    </Typography>
                  </Box>
                )}

                {caseData.client && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Client</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Person sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body2">
                        {caseData.client.firstName} {caseData.client.lastName}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {caseData.client.email}
                    </Typography>
                  </Box>
                )}

                {caseData.assignedUser && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Assigned To</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Assignment sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body2">
                        {caseData.assignedUser.firstName} {caseData.assignedUser.lastName}
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                {caseData.estimatedValue && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Estimated Value</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <AttachMoney sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        ${caseData.estimatedValue.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {caseData.court && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Court</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {caseData.court}
                    </Typography>
                  </Box>
                )}

                {caseData.judge && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Judge</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {caseData.judge}
                    </Typography>
                  </Box>
                )}

                {caseData.filingDate && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Filing Date</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CalendarToday sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body2">
                        {formatDate(caseData.filingDate)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Statute of Limitations
                  </Typography>
                  {caseData.solExpirationDate ? (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        size="small"
                        color="warning"
                        icon={<Schedule />}
                        label={`Expires ${formatDate(caseData.solExpirationDate)}`}
                      />
                      {caseData.solIncidentDate && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          Incident: {formatDate(caseData.solIncidentDate)}
                          {caseData.solPresetId ? ` · ${caseData.solPresetId}` : ''}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Not docketed
                    </Typography>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CalendarToday />}
                    onClick={() => setSolCalculatorOpen(true)}
                    sx={{ mt: 1 }}
                  >
                    {caseData.solExpirationDate ? 'Update SOL' : 'Calculate & save SOL'}
                  </Button>
                </Box>

                {caseData.closingDate && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Closing Date</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Schedule sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body2">
                        {formatDate(caseData.closingDate)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Properties</Typography>
                  {caseProperties.length === 0 ? (
                    <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                      None on file
                    </Typography>
                  ) : (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        size="small"
                        label={`${caseProperties.length} propert${caseProperties.length === 1 ? 'y' : 'ies'}`}
                        onClick={() => setActiveTab(3)}
                        sx={{ mb: 0.5 }}
                      />
                      {caseProperties[0]?.address && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {caseProperties[0].address}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                {caseData.tags && (() => {
                  try {
                    const tags = typeof caseData.tags === 'string' ? JSON.parse(caseData.tags) : caseData.tags;
                    return tags && tags.length > 0 ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">Tags</Typography>
                        <Box sx={{ mt: 1 }}>
                          {tags.map((tag: string, index: number) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    ) : null;
                  } catch (error) {
                    console.warn('Error parsing tags:', error);
                    return null;
                  }
                })()}
              </CardContent>
            </Card>
            )}
          </Box>
        </Drawer>
        )}

        {/* Main Content Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <Box sx={{ px: embedded ? 2 : 3, py: embedded ? 1.5 : 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: embedded ? 0.5 : 2, gap: 1, flexWrap: 'wrap' }}>
              <FolderOpen sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant={embedded ? 'h6' : 'h5'} sx={{ flex: 1 }}>
                Case Documents Folder
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/drive?caseId=${caseId}`)}
              >
                Open in Matter Drive
              </Button>
            </Box>
            {!embedded && (
              <Typography variant="body2" color="text.secondary">
                This folder contains all documents related to case {caseData.caseNumber}
              </Typography>
            )}
          </Box>

          {/* Tabs for Documents, Notes, and Properties */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="scrollable" scrollButtons="auto">
              <Tab label={`Documents (${documentsTotal})`} />
              <Tab label="Activity" />
              <Tab label={`Notes (${notesTotal})`} />
              <Tab label={`Properties (${caseProperties.length})`} />
              <Tab label="Privilege Log" icon={<GavelIcon fontSize="small" />} iconPosition="start" />
              <Tab label="Workflows" />
              <Tab label="Calls" />
              <Tab label="Court Forms" />
            </Tabs>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: embedded ? 1.5 : 3, minHeight: 0 }}>
            {activeTab === 0 && (
              // Documents Tab
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<GavelIcon />}
                    onClick={() => setCaptionDialogOpen(true)}
                  >
                    Auto-caption
                  </Button>
                  {documents.length > 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LocalOffer />}
                      onClick={() => setBatesDialogOpen(true)}
                    >
                      Bates stamp
                    </Button>
                  )}
                </Box>
                {caseData?.metadata && (caseData.metadata as any)?.caption?.text && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Case caption:{' '}
                    <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {(caseData.metadata as any).caption.text}
                    </Typography>
                  </Alert>
                )}
                {documentsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : documentsError ? (
                  <Alert severity="error">
                    Failed to load case documents. Please try again.
                  </Alert>
                ) : documents.length === 0 ? (
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <Folder sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Case Documents Folder is Empty
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload documents or import templates to get started
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<CloudUpload />}
                        onClick={() => setUploadDialogOpen(true)}
                        sx={{ mr: 1 }}
                      >
                        Upload Document
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Description />}
                        onClick={() => setTemplateDialogOpen(true)}
                      >
                        Import Template
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ width: '100%' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 40 }} />
                          <TableCell>Document</TableCell>
                          <TableCell sx={{ width: 110 }}>Type</TableCell>
                          <TableCell sx={{ width: 140 }}>Review</TableCell>
                          <TableCell sx={{ width: 140 }}>Signature</TableCell>
                          <TableCell sx={{ width: 150 }}>Bates</TableCell>
                          <TableCell sx={{ width: 80 }} align="right">
                            Size
                          </TableCell>
                          <TableCell sx={{ width: 130 }}>Uploaded</TableCell>
                          <TableCell sx={{ width: 48 }} align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {documents.map((document: Document) => {
                          const bates = getBatesDisplay(document.metadata as Record<string, unknown>);
                          const sig = sigByDoc.get(document.id);
                          const isExecuted = signedIds.has(document.id);
                          return (
                          <TableRow
                            key={document.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleViewDocument(document)}
                          >
                            <TableCell sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', color: 'primary.main', '& .MuiSvgIcon-root': { fontSize: 20 } }}>
                              {getDocumentTypeIcon(document.documentType)}
                            </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1, maxWidth: 0 }}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                                title={document.title}
                              >
                                {document.title}
                              </Typography>
                              {(() => {
                                const unresolved = Array.isArray(
                                  (document.metadata as any)?.merge?.unresolved
                                )
                                  ? ((document.metadata as any).merge.unresolved as string[])
                                  : [];
                                if (!unresolved.length) return null;
                                return (
                                  <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label={`${unresolved.length} unresolved`}
                                    title={`Unresolved merge fields: ${unresolved.join(', ')}`}
                                    sx={{ mt: 0.5, height: 22, fontSize: '0.7rem' }}
                                  />
                                );
                              })()}
                              {document.description ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  display="block"
                                  title={document.description}
                                >
                                  {document.description}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Chip
                                label={document.documentType.replace(/_/g, ' ')}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ maxWidth: 100 }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1 }} onClick={(e) => e.stopPropagation()}>
                              <ReviewStatusChip
                                documentId={document.id}
                                reviewStatus={document.reviewStatus}
                                onChanged={() => queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] })}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              {sig ? <SignatureStatusChip status={sig.status} /> : isExecuted ? <SignatureStatusChip status="completed" /> : null}
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              {bates.label ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontFamily: 'monospace' }}
                                    title={bates.label}
                                  >
                                    {bates.label}
                                  </Typography>
                                  {bates.stamped && (
                                    <Chip size="small" label="PDF" color="success" sx={{ height: 18 }} />
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(document.fileSize)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(document.createdAt)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.5 }} onClick={(e) => e.stopPropagation()}>
                              <IconButton
                                size="small"
                                aria-label="Document actions"
                                onClick={(e) => {
                                  setSelectedItem(document);
                                  setAnchorEl(e.currentTarget);
                                }}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <ListPagination
                      page={docPage}
                      limit={docLimit}
                      total={documentsTotal}
                      onPageChange={setDocPage}
                      onLimitChange={(next) => {
                        setDocLimit(next);
                        setDocPage(1);
                      }}
                    />
                  </TableContainer>
                )}
              </>
            )}

            {activeTab === 1 && caseId && (
              <CaseActivityPanel caseId={caseId} />
            )}

            {activeTab === 2 && (
              // Notes Tab
              <>
                {notesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : notesError ? (
                  <Alert severity="error">
                    Failed to load case notes. Please try again.
                  </Alert>
                ) : notes.length === 0 ? (
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <Note sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Notes Yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add notes to documents or create general case notes
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<NoteAdd />}
                        onClick={handleCreateNote}
                      >
                        Add Note
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ width: '100%' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 40 }} />
                          <TableCell>Note</TableCell>
                          <TableCell sx={{ width: 130 }}>Type</TableCell>
                          <TableCell sx={{ width: 140 }}>Created</TableCell>
                          <TableCell sx={{ width: 48 }} align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {notes.map((note: CaseNote) => (
                          <TableRow key={note.id} hover>
                            <TableCell sx={{ py: 1 }}>
                              <Note color="primary" fontSize="small" />
                            </TableCell>
                            <TableCell sx={{ py: 1, maxWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap title={note.title}>
                                {note.title}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                display="block"
                                title={note.content}
                              >
                                {note.content}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip
                                  label={note.noteType.replace(/_/g, ' ')}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                />
                                {note.isConfidential && (
                                  <Chip
                                    label="Confidential"
                                    size="small"
                                    color="error"
                                    icon={<Lock />}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(note.createdAt)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.5 }}>
                              <IconButton
                                size="small"
                                aria-label="Note actions"
                                onClick={(e) => {
                                  setSelectedItem(note);
                                  setAnchorEl(e.currentTarget);
                                }}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <ListPagination
                      page={notePage}
                      limit={noteLimit}
                      total={notesTotal}
                      onPageChange={setNotePage}
                      onLimitChange={(next) => {
                        setNoteLimit(next);
                        setNotePage(1);
                      }}
                    />
                  </TableContainer>
                )}
              </>
            )}

            {activeTab === 3 && (
              <CasePropertiesPanel caseId={caseId} />
            )}

            {activeTab === 4 && (
              <PrivilegeLogPanel caseId={caseId} />
            )}

            {activeTab === 5 && (
              <CaseWorkflowPanel caseId={caseId} caseType={caseData?.caseType} />
            )}

            {activeTab === 6 && (
              <CallLogPanel caseId={caseId} clientId={caseData?.clientId} />
            )}

            {activeTab === 7 && caseId && (
              <CaseCourtFormsPanel caseId={caseId} />
            )}
          </Box>
        </Box>
      </Box>

      {/* Upload Dialog */}
      <DocumentUpload
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        caseId={caseId}
        clientId={caseData?.clientId || undefined}
        title="Upload Document to Case Folder"
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] });
          queryClient.invalidateQueries({ queryKey: ['matter-drive'] });
          queryClient.invalidateQueries({ queryKey: ['matter-drive-tree'] });
          queryClient.invalidateQueries({ queryKey: ['documents'] });
          setDocPage(1);
        }}
      />

      <AttachExistingDocumentsDialog
        open={attachDialogOpen}
        onClose={() => setAttachDialogOpen(false)}
        caseId={caseId}
        caseNumber={caseData?.caseNumber}
        clientId={caseData?.clientId || null}
        clientName={caseData?.client
          ? `${caseData.client.firstName || ''} ${caseData.client.lastName || ''}`.trim()
          : undefined}
        onSuccess={() => setDocPage(1)}
      />

      <ImportTemplateToCaseDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        caseId={caseId}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] });
        }}
      />

      <ExportCaseFileDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        caseId={caseId}
        caseNumber={caseData?.caseNumber}
      />

      {/* Note Dialog */}
      <Dialog
        open={noteDialogOpen}
        onClose={() => setNoteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {noteMode === 'create' ? 'Create Note' : noteMode === 'edit' ? 'Edit Note' : 'View Note'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Note Title"
                value={noteForm.title}
                onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                disabled={noteMode === 'view'}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Note Type</InputLabel>
                <Select
                  value={noteForm.noteType}
                  onChange={(e) => setNoteForm({ ...noteForm, noteType: e.target.value })}
                  disabled={noteMode === 'view'}
                >
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="document_note">Document Note</MenuItem>
                  <MenuItem value="case_note">Case Note</MenuItem>
                  <MenuItem value="client_note">Client Note</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category"
                value={noteForm.category}
                onChange={(e) => setNoteForm({ ...noteForm, category: e.target.value })}
                disabled={noteMode === 'view'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Content"
                multiline
                rows={6}
                value={noteForm.content}
                onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                disabled={noteMode === 'view'}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={noteForm.isConfidential}
                    onChange={(e) => setNoteForm({ ...noteForm, isConfidential: e.target.checked })}
                    disabled={noteMode === 'view'}
                  />
                }
                label="Confidential Note"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>
            Cancel
          </Button>
          {noteMode !== 'view' && (
            <Button 
              onClick={handleSaveNote}
              variant="contained"
              disabled={!noteForm.title || !noteForm.content}
            >
              {noteMode === 'create' ? 'Create' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Document Editor */}
      <DocumentEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        document={selectedDocument}
        caseId={caseId}
        onSave={() => {
          setEditorOpen(false);
          queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] });
        }}
      />

      <DocumentPreviewDialog
        open={previewOpen}
        document={previewDocument}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewDocument(null);
        }}
        onEditContent={(doc) => {
          setPreviewOpen(false);
          handleEditDocument(doc as Document);
        }}
        onEditProperties={(doc) => {
          setPreviewOpen(false);
          handleEditDocumentProperties(doc as Document);
        }}
        onDownload={(doc) => downloadCaseDocument(doc as Document)}
      />

      <RequestSignatureDialog
        open={signDialogOpen}
        documentId={signDocumentId}
        onClose={() => {
          setSignDialogOpen(false);
          setSignDocumentId(null);
        }}
        onSent={() => {
          queryClient.invalidateQueries({ queryKey: ['signature-requests', caseId] });
          queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] });
        }}
      />

      {/* Document properties (Edit) */}
      <DocumentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        documentData={selectedDocument}
        mode={dialogMode === 'view' ? 'edit' : dialogMode}
        onSave={async (formData) => {
          if (!selectedDocument?.id) return;
          const token = getAuthToken();
          const response = await fetch(`/api/documents/${selectedDocument.id}`, {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              title: formData.title,
              description: formData.description,
              documentType: formData.documentType,
              category: formData.category,
              tags: formData.tags,
              isConfidential: formData.isConfidential,
              isPublic: formData.isPublic,
            }),
          });
          if (!response.ok) {
            throw new Error(await getApiErrorMessage(response, 'Unable to save document.'));
          }
          setDialogOpen(false);
          queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] });
        }}
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {/* Document Actions */}
        {selectedItem && 'fileName' in selectedItem && (
          <>
            <MenuItem onClick={() => {
              if (selectedItem) {
                handleViewDocument(selectedItem as Document);
              }
              setAnchorEl(null);
            }}>
              <ListItemIcon>
                <Visibility fontSize="small" />
              </ListItemIcon>
              <ListItemText>View document</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              if (selectedItem) {
                handleEditDocumentProperties(selectedItem as Document);
              }
              setAnchorEl(null);
            }}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit properties</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              if (selectedItem) {
                handleEditDocument(selectedItem as Document);
              }
              setAnchorEl(null);
            }}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit document</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              if (selectedItem) {
                downloadCaseDocument(selectedItem as Document);
              }
              setAnchorEl(null);
            }}>
              <ListItemIcon>
                <Download fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download</ListItemText>
            </MenuItem>
            {eSignEnabled && selectedItem && 'fileName' in selectedItem && (
              <MenuItem onClick={() => {
                setSignDocumentId((selectedItem as Document).id);
                setSignDialogOpen(true);
                setAnchorEl(null);
              }}>
                <ListItemIcon>
                  <Draw fontSize="small" />
                </ListItemIcon>
                <ListItemText>Request signature</ListItemText>
              </MenuItem>
            )}
            {selectedItem &&
              'fileName' in selectedItem &&
              (() => {
                const meta = (selectedItem as Document).metadata as {
                  source?: string;
                  sourceDetails?: { templateId?: string };
                } | undefined;
                return meta?.source === 'import-template' && meta?.sourceDetails?.templateId;
              })() && (
              <MenuItem
                disabled={refreshingMerge}
                onClick={() => handleRefreshMerge(selectedItem as Document)}
              >
                <ListItemIcon>
                  <Autorenew fontSize="small" />
                </ListItemIcon>
                <ListItemText>Refresh merge</ListItemText>
              </MenuItem>
            )}
            {canDeleteDocuments && (
              <MenuItem onClick={() => {
                if (selectedItem) {
                  setDocumentToDelete(selectedItem as Document);
                  setDeleteConfirmOpen(true);
                  setAnchorEl(null);
                }
              }}>
                <ListItemIcon>
                  <Delete fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </MenuItem>
            )}
          </>
        )}
        
        {/* Note Actions */}
        {selectedItem && 'content' in selectedItem && (
          <>
            <MenuItem onClick={() => {
              if (selectedItem) {
                handleViewNote(selectedItem as CaseNote);
              }
              setAnchorEl(null);
            }}>
              <ListItemIcon>
                <Visibility fontSize="small" />
              </ListItemIcon>
              <ListItemText>View</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              if (selectedItem) {
                handleEditNote(selectedItem as CaseNote);
              }
              setAnchorEl(null);
            }}>
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              if (selectedItem) {
                setNoteToDelete(selectedItem as CaseNote);
                setDeleteConfirmOpen(true);
                setAnchorEl(null);
              }
            }}
            disabled={!canWriteCases}
            >
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {noteToDelete ? 'Delete Note' : 'Delete Document'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {noteToDelete 
              ? `Are you sure you want to delete the note "${noteToDelete.title}"? This action cannot be undone.`
              : `Are you sure you want to delete the document "${documentToDelete?.title}"? This action cannot be undone.`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (noteToDelete) {
                deleteNoteMutation.mutate(noteToDelete.id);
                setDeleteConfirmOpen(false);
                setNoteToDelete(null);
              } else if (documentToDelete) {
                deleteDocumentMutation.mutate(documentToDelete.id);
                setDeleteConfirmOpen(false);
                setDocumentToDelete(null);
              }
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <CaseIntakeWizard
        open={intakeOpen}
        onClose={() => setIntakeOpen(false)}
        mode="continue"
        caseId={caseId}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] });
          queryClient.invalidateQueries({ queryKey: ['case', caseId] });
        }}
      />

      <BatesNumberingDialog
        open={batesDialogOpen}
        onClose={() => setBatesDialogOpen(false)}
        caseId={caseId}
        documents={documents.map((d: Document) => ({
          id: d.id,
          title: d.title,
          fileName: d.fileName || d.originalName,
          mimeType: d.mimeType,
          metadata: d.metadata,
        }))}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] });
        }}
      />

      <AutoCaptionDialog
        open={captionDialogOpen}
        onClose={() => setCaptionDialogOpen(false)}
        caseId={caseId}
        documents={documents.map((d: Document) => ({
          id: d.id,
          title: d.title,
          fileName: d.fileName || d.originalName,
          mimeType: d.mimeType,
          metadata: d.metadata,
        }))}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] });
          queryClient.invalidateQueries({ queryKey: ['case', caseId] });
        }}
      />

      <DateCalculatorDialog
        open={solCalculatorOpen}
        onClose={() => setSolCalculatorOpen(false)}
        caseId={caseId}
        onSolPersisted={() => {
          queryClient.invalidateQueries({ queryKey: ['case', caseId] });
          queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
        }}
      />
    </Box>
  );
};

export default CaseFolder; 